import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DailyRecord, WeightMeasurement, WeightMeasurementPhoto, WeightPhotoType } from '../types';
import { toPersianDigits } from '../lib/storage';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { sendWorkerAiFullRequest } from '../lib/workerAi';
import { 
  fetchWeightMeasurements, saveWeightMeasurement, deleteWeightMeasurement, 
  uploadWeightPhoto, deleteWeightPhoto, compressImageIfNeeded,
  apiGet, apiPut 
} from '../lib/api';
import { 
  Scale, Ruler, Target, Trash2, Plus, Minus, Info, Sparkles, 
  Calendar, Award, TrendingUp, TrendingDown, Check, AlertCircle,
  Brain, Flame, Droplets, ShieldAlert, Zap, RefreshCw, Activity, Percent,
  Camera, Image as ImageIcon, Maximize2, X, ArrowLeftRight, Download, Eye, Upload
} from 'lucide-react';

interface WeightModuleProps {
  record: DailyRecord;
  onUpdateRecord: (updated: DailyRecord) => void;
}

export default function WeightModule({ record, onUpdateRecord }: WeightModuleProps) {
  const { lang, t } = useLanguage();
  const fmt = (v: any) => formatNumberByLang(v, lang);

  // Primary State loaded directly from Cloudflare D1
  const [loadingD1, setLoadingD1] = useState<boolean>(true);
  const [weightLogs, setWeightLogs] = useState<WeightMeasurement[]>([]);
  const [saving, setSaving] = useState<boolean>(false);

  // Form State
  const [selectedDate, setSelectedDate] = useState<string>(record.date || new Date().toISOString().split('T')[0]);
  const [weightInput, setWeightInput] = useState<string>('75.0');
  const [bodyFatInput, setBodyFatInput] = useState<string>('');
  
  // Physical Settings from D1
  const [height, setHeight] = useState<string>('');
  const [weightGoal, setWeightGoal] = useState<string>('');
  const [bodyFatGoal, setBodyFatGoal] = useState<string>('');
  const [savingProfile, setSavingProfile] = useState<boolean>(false);
  const [profileSaved, setProfileSaved] = useState<boolean>(false);

  // UI view state
  const [selectedMetric, setSelectedMetric] = useState<'weight' | 'fat'>('weight');
  const [selectedRange, setSelectedRange] = useState<'all' | '30' | '90'>('all');
  const [hoveredPoint, setHoveredPoint] = useState<any>(null);

  // AI Diagnostic State
  const [isAnalyzingPlateauAi, setIsAnalyzingPlateauAi] = useState(false);
  const [aiPlateauResult, setAiPlateauResult] = useState<string | null>(null);

  // Body Composition AI State
  const [isAnalyzingBodyCompAi, setIsAnalyzingBodyCompAi] = useState(false);
  const [aiBodyCompResult, setAiBodyCompResult] = useState<string | null>(null);

  const [isAnalyzingMuscleAi, setIsAnalyzingMuscleAi] = useState(false);
  const [aiMuscleResult, setAiMuscleResult] = useState<string | null>(null);

  // Photos & Comparison State
  const [selectedPhotoType, setSelectedPhotoType] = useState<WeightPhotoType>('front');
  const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false);
  const [stagedPhotos, setStagedPhotos] = useState<Array<{ file: File; photoType: string; previewUrl: string }>>([]);
  const [activeLightboxPhoto, setActiveLightboxPhoto] = useState<any | null>(null);

  // Photo Comparison Modal State
  const [showCompareModal, setShowCompareModal] = useState<boolean>(false);
  const [compareDate1, setCompareDate1] = useState<string>('');
  const [compareDate2, setCompareDate2] = useState<string>('');
  const [comparePhotoTypeFilter, setComparePhotoTypeFilter] = useState<string>('all');

  // Fetch data directly from Cloudflare D1 on mount or date change
  const loadD1Data = async () => {
    setLoadingD1(true);
    try {
      // 1. Fetch weight measurements from D1
      const weightRes = await fetchWeightMeasurements();
      if (weightRes.success && Array.isArray(weightRes.data)) {
        setWeightLogs(weightRes.data);

        // Pre-fill input if there's a record for selected date
        const match = weightRes.data.find(m => m.date === selectedDate);
        if (match) {
          setWeightInput(String(match.weight_kg));
          if (match.body_fat_pct) setBodyFatInput(String(match.body_fat_pct));
        } else if (weightRes.data.length > 0) {
          // Default to latest recorded weight
          setWeightInput(String(weightRes.data[0].weight_kg));
          if (weightRes.data[0].body_fat_pct) setBodyFatInput(String(weightRes.data[0].body_fat_pct));
        }
      }

      // 2. Fetch user physical settings from D1
      const settingsRes = await apiGet('/api/settings');
      if (settingsRes.success && settingsRes.data) {
        const s = settingsRes.data;
        if (s.height || s.heightCm) setHeight(String(s.height || s.heightCm));
        if (s.weightGoal) setWeightGoal(String(s.weightGoal));
        if (s.bodyFatGoal) setBodyFatGoal(String(s.bodyFatGoal));
      }
    } catch (err) {
      console.error('Failed fetching D1 weight measurements:', err);
    } finally {
      setLoadingD1(false);
    }
  };

  useEffect(() => {
    loadD1Data();
  }, []);

  // Update selected date if top record.date changes
  useEffect(() => {
    if (record.date) {
      setSelectedDate(record.date);
      const match = weightLogs.find(m => m.date === record.date);
      if (match) {
        setWeightInput(String(match.weight_kg));
        if (match.body_fat_pct) setBodyFatInput(String(match.body_fat_pct));
      }
    }
  }, [record.date, weightLogs]);

  // Handle Photo File Upload Input
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhoto(true);
    try {
      const match = weightLogs.find(m => m.date === selectedDate);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (match && match.id) {
          // Direct API upload to R2
          await uploadWeightPhoto(match.id, file, selectedPhotoType);
        } else {
          // Compress and stage locally until measurement is saved
          const processed = await compressImageIfNeeded(file, 614400);
          if (processed.size > 614400) {
            throw new Error(`حجم تصویر ${file.name} پس از فشرده‌سازی نیز بیش از ۶۰۰ کیلوبایت است.`);
          }
          const previewUrl = URL.createObjectURL(processed);
          setStagedPhotos(prev => [...prev, { file: processed, photoType: selectedPhotoType, previewUrl }]);
        }
      }

      if (match && match.id) {
        await loadD1Data();
      }
    } catch (err: any) {
      alert(err?.message || 'خطا در بارگذاری تصویر');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // Delete an existing photo from D1/R2
  const handleDeletePhoto = async (photoId: string) => {
    if (!confirm(t('confirmDeletePhoto', 'آیا از حذف این تصویر پیشرفت اطمینان دارید؟'))) return;
    try {
      const res = await deleteWeightPhoto(photoId);
      if (res.success) {
        if (activeLightboxPhoto?.id === photoId || activeLightboxPhoto?.photo_id === photoId) {
          setActiveLightboxPhoto(null);
        }
        await loadD1Data();
      } else {
        alert(res.error || 'خطا در حذف تصویر');
      }
    } catch (err: any) {
      alert(err?.message || 'خطا در حذف تصویر');
    }
  };

  // Remove a staged photo
  const handleRemoveStagedPhoto = (index: number) => {
    setStagedPhotos(prev => {
      const target = prev[index];
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Handle Save Weight & Body Fat directly to Cloudflare D1
  const handleSaveWeight = async () => {
    const val = parseFloat(weightInput);
    if (isNaN(val) || val <= 0 || val > 300) {
      alert(t('weightAlertInvalid', 'لطفاً وزن معتبری (باید عددی بین ۱ تا ۳۰۰ کیلوگرم باشد) وارد کنید.'));
      return;
    }

    const fatVal = bodyFatInput ? parseFloat(bodyFatInput) : null;
    if (fatVal !== null && (isNaN(fatVal) || fatVal < 1 || fatVal > 75)) {
      alert(t('weightFatAlertInvalid', 'لطفاً درصد چربی معتبری (بین ۱ تا ۷۵ درصد) وارد کنید.'));
      return;
    }

    setSaving(true);
    try {
      const parsedWeight = parseFloat(val.toFixed(1));
      const parsedFat = fatVal !== null ? parseFloat(fatVal.toFixed(1)) : null;

      let computedBmi: number | null = null;
      const hNum = parseFloat(height);
      if (!isNaN(hNum) && hNum > 0) {
        const heightM = hNum / 100;
        computedBmi = parseFloat((parsedWeight / (heightM * heightM)).toFixed(1));
      }

      const res = await saveWeightMeasurement({
        date: selectedDate,
        weight_kg: parsedWeight,
        bmi: computedBmi,
        body_fat_pct: parsedFat,
        notes: ''
      });

      if (res.success) {
        const targetId = res.data?.id || weightLogs.find(m => m.date === selectedDate)?.id;
        if (stagedPhotos.length > 0) {
          if (!targetId || targetId === 'undefined') {
            alert('خطا: شناسه ثبت وزن یافت نشد. تصاویر امکان آپلود نداشتند.');
          } else {
            const uploadErrors: string[] = [];
            for (const p of stagedPhotos) {
              try {
                await uploadWeightPhoto(targetId, p.file, p.photoType);
              } catch (pErr: any) {
                console.error('Failed uploading staged photo:', pErr);
                uploadErrors.push(pErr?.message || 'خطا در آپلود تصویر');
              }
            }
            if (uploadErrors.length > 0) {
              alert(`توجه: برخی تصاویر پس از ثبت وزن آپلود نشدند:\n${uploadErrors.join('\n')}`);
            }
            setStagedPhotos([]);
          }
        }

        // Sync with parent record if for current date
        if (selectedDate === record.date) {
          onUpdateRecord({
            ...record,
            weight: parsedWeight
          });
        }
        await loadD1Data();
      } else {
        alert(res.error || t('weightSaveError', 'خطا در ذخیره‌سازی داده در Cloudflare D1'));
      }
    } catch (err: any) {
      alert(err?.message || 'خطا در ثبت وزن');
    } finally {
      setSaving(false);
    }
  };

  // Handle Save Physical Profile (Height, Target Weight, Target Body Fat %) to Cloudflare D1
  const handleSaveProfile = async () => {
    const hVal = height ? parseFloat(height) : undefined;
    const wgVal = weightGoal ? parseFloat(weightGoal) : undefined;
    const bfgVal = bodyFatGoal ? parseFloat(bodyFatGoal) : undefined;

    if (height && (isNaN(hVal!) || hVal! < 50 || hVal! > 250)) {
      alert(t('weightAlertHeight', 'لطفاً قد معتبری (بین ۵۰ تا ۲۵۰ سانتی‌متر) وارد کنید.'));
      return;
    }

    if (weightGoal && (isNaN(wgVal!) || wgVal! < 20 || wgVal! > 300)) {
      alert(t('weightAlertGoal', 'لطفاً وزن هدف معتبری وارد کنید.'));
      return;
    }

    if (bodyFatGoal && (isNaN(bfgVal!) || bfgVal! < 3 || bfgVal! > 60)) {
      alert(t('weightAlertFatGoal', 'لطفاً هدف درصد چربی معتبری وارد کنید.'));
      return;
    }

    setSavingProfile(true);
    try {
      const res = await apiPut('/api/settings', {
        height: hVal ? parseFloat(hVal.toFixed(1)) : null,
        heightCm: hVal ? parseFloat(hVal.toFixed(1)) : null,
        weightGoal: wgVal ? parseFloat(wgVal.toFixed(1)) : null,
        bodyFatGoal: bfgVal ? parseFloat(bfgVal.toFixed(1)) : null
      });

      if (res.success) {
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 2500);
        await loadD1Data();
      } else {
        alert(res.error || 'خطا در ذخیره‌سازی مشخصات فیزیکی');
      }
    } catch (err: any) {
      alert('خطا در ذخیره‌سازی مشخصات');
    } finally {
      setSavingProfile(false);
    }
  };

  // Handle Delete Measurement from D1
  const handleDeleteWeight = async (idOrDate: string) => {
    if (!confirm(t('weightConfirmDel', 'آیا مطمئن هستید که می‌خواهید ثبت وزن این تاریخ را حذف کنید؟', { date: toPersianDigits(idOrDate) }))) {
      return;
    }

    try {
      const res = await deleteWeightMeasurement(idOrDate);
      if (res.success) {
        if (idOrDate === record.date) {
          const updated = { ...record };
          delete updated.weight;
          onUpdateRecord(updated);
        }
        await loadD1Data();
      } else {
        alert(res.error || 'خطا در حذف رکورد');
      }
    } catch (err: any) {
      alert('خطا در حذف رکورد');
    }
  };

  const adjustWeight = (delta: number) => {
    const current = parseFloat(weightInput) || 75.0;
    const next = Math.max(20, Math.min(300, current + delta));
    setWeightInput(next.toFixed(1));
  };

  const adjustBodyFat = (delta: number) => {
    const current = parseFloat(bodyFatInput) || 20.0;
    const next = Math.max(1, Math.min(75, current + delta));
    setBodyFatInput(next.toFixed(1));
  };

  // Sorted Weight History ascending by date
  const sortedHistory = useMemo(() => {
    const sorted = [...weightLogs].sort((a, b) => a.date.localeCompare(b.date));
    if (selectedRange === 'all') return sorted;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - parseInt(selectedRange));
    const cutoffStr = cutoffDate.toISOString().split('T')[0];
    return sorted.filter(p => p.date >= cutoffStr);
  }, [weightLogs, selectedRange]);

  // Latest measurements
  const latestMeasurement = weightLogs.length > 0 ? weightLogs[0] : null; // weightLogs is DESC from D1
  const currentWeightNum = latestMeasurement ? latestMeasurement.weight_kg : parseFloat(weightInput) || null;
  const currentFatNum = latestMeasurement?.body_fat_pct || (bodyFatInput ? parseFloat(bodyFatInput) : null);
  const userHeightCm = parseFloat(height) || null;

  // BMI calculations
  const bmi = useMemo(() => {
    if (!currentWeightNum || !userHeightCm) return null;
    const heightM = userHeightCm / 100;
    return parseFloat((currentWeightNum / (heightM * heightM)).toFixed(1));
  }, [currentWeightNum, userHeightCm]);

  let bmiCategory = t('weightBmiNormal', 'وزن ایده آل');
  let bmiColor = 'text-emerald-500';
  let bmiBg = 'bg-emerald-500/10';
  let bmiAdvice = t('weightBmiAdviceNormal', 'شاخص توده بدنی شما در محدوده بسیار مطلوب قرار دارد. این وضعیت فشار را از روی مهره‌های کمری شما کاهش می‌دهد.');

  if (bmi !== null) {
    if (bmi < 18.5) {
      bmiCategory = t('weightBmiUnderweight', 'کم‌وزن (نیاز به تقویت)');
      bmiColor = 'text-blue-500';
      bmiBg = 'bg-blue-500/10';
      bmiAdvice = t('weightBmiAdviceUnder', 'وزن شما پایین‌تر از حد نرمال است. افزایش توده عضلانی با تغذیه پروتئینی مناسب به پشتیبانی بهتر ستون فقرات کمک می‌کند.');
    } else if (bmi >= 25 && bmi < 30) {
      bmiCategory = t('weightBmiOverweight', 'اضافه‌وزن (هشدار فشار کمر)');
      bmiColor = 'text-amber-500';
      bmiBg = 'bg-amber-500/10';
      bmiAdvice = t('weightBmiAdviceOver', 'مقداری اضافه‌وزن وجود دارد. کاهش ۳ تا ۵ کیلوگرم وزن، فشار وارده بر دیسک‌های کمر L4-L5 را تا ۱۵٪ کاهش می‌دهد.');
    } else if (bmi >= 30) {
      bmiCategory = t('weightBmiObeseCat', 'چاقی (نیاز به برنامه کاهش وزن)');
      bmiColor = 'text-rose-500';
      bmiBg = 'bg-rose-500/10';
      bmiAdvice = t('weightBmiAdviceObese', 'شاخص BMI بالای ۳۰ نشان‌دهنده فشار زیاد بر مفاصل و کمر است. پیشنهاد می‌شود روند کاهش وزن تدریجی را پیگیری کنید.');
    }
  }

  // BMI Progress Bar Position
  let bmiProgressWidth = '50%';
  if (bmi !== null) {
    const minBmi = 15;
    const maxBmi = 40;
    const clamped = Math.max(minBmi, Math.min(maxBmi, bmi));
    const pct = ((clamped - minBmi) / (maxBmi - minBmi)) * 100;
    bmiProgressWidth = `${pct}%`;
  }

  // Body Fat % category calculation
  let fatCategory = t('fatCatOptimal', 'مطلوب و تناسب اندام');
  let fatColor = 'text-emerald-500';
  if (currentFatNum !== null) {
    if (currentFatNum < 10) {
      fatCategory = t('fatCatLow', 'بسیار کم (ورزشی)');
      fatColor = 'text-blue-500';
    } else if (currentFatNum <= 22) {
      fatCategory = t('fatCatOptimal', 'مطلوب و تناسب اندام');
      fatColor = 'text-emerald-500';
    } else if (currentFatNum <= 28) {
      fatCategory = t('fatCatModerate', 'متوسط');
      fatColor = 'text-amber-500';
    } else {
      fatCategory = t('fatCatHigh', 'بالا (نیاز به چربی‌سوزی)');
      fatColor = 'text-rose-500';
    }
  }

  // Plateau Check
  const plateauAnalysis = useMemo(() => {
    if (sortedHistory.length < 2) {
      return { isPlateau: false, diffKg: 0 };
    }
    const latestPoint = sortedHistory[sortedHistory.length - 1];
    const firstPoint = sortedHistory[0];
    const diffKg = Math.abs(latestPoint.weight_kg - firstPoint.weight_kg);
    return {
      isPlateau: diffKg < 0.4,
      diffKg: parseFloat(diffKg.toFixed(1))
    };
  }, [sortedHistory]);

  const handleAskPlateauAi = async () => {
    if (isAnalyzingPlateauAi) return;
    setIsAnalyzingPlateauAi(true);
    setAiPlateauResult(null);

    const langName = lang === 'sv' ? 'Swedish' : lang === 'fa' ? 'Persian' : 'English';
    const prompt = `You are a clinical metabolic doctor and sports nutritionist specialist.
Analyze why the user's weight loss has stalled/plateaued and provide a 3-step action protocol to break the plateau.

User Physical Stats:
- Current Weight: ${currentWeightNum || 'N/A'} kg
- Current Body Fat %: ${currentFatNum || 'N/A'} %
- Height: ${height || 'N/A'} cm
- Target Weight: ${weightGoal || 'N/A'} kg
- Target Body Fat %: ${bodyFatGoal || 'N/A'} %
- Recorded Weight History in App: ${JSON.stringify(sortedHistory.slice(-10).map(s => ({ date: s.date, weight: s.weight_kg, fat: s.body_fat_pct })))}

CRITICAL INSTRUCTIONS:
1. Write the ENTIRE response in ${langName}.
2. Use Markdown headings and structured bullet points.
3. Be encouraging, highly practical, and focus on:
   - Refeed day calculation (Carbs vs Protein)
   - NEAT (Non-Exercise Activity Thermogenesis) step targets
   - Cortisol / Sleep / Water balance adjustment
   - Waist measurement vs scale weight context.`;

    try {
      const res = await sendWorkerAiFullRequest('nutrition', prompt, []);
      setAiPlateauResult(res.reply || t('aiNoReply', 'پاسخی دریافت نشد.'));
    } catch (err: any) {
      setAiPlateauResult(t('aiErrorPrefix', 'خطا در دریافت پاسخ از هوش مصنوعی: ') + (err?.message || t('tryAgain', 'لطفاً دوباره تلاش کنید.')));
    } finally {
      setIsAnalyzingPlateauAi(false);
    }
  };

  // Body Composition AI Analysis Handler
  const handleAnalyzeBodyCompAi = async () => {
    if (isAnalyzingBodyCompAi) return;
    setIsAnalyzingBodyCompAi(true);
    setAiBodyCompResult(null);

    const langName = lang === 'sv' ? 'Swedish' : lang === 'fa' ? 'Persian' : 'English';

    const fatDataText = currentFatNum !== null && currentFatNum !== undefined
      ? `Current body fat %: ${currentFatNum}%`
      : 'Current body fat %: NOT LOGGED (CRITICAL: Explicitly state body fat % is not logged; do NOT guess or invent a body fat %).';

    const prompt = `You are a senior clinical nutritionist, metabolic physician, and sports physiologist.
Analyze the user's anatomical profile and real D1 database records:

User D1 Data:
- Current Weight: ${currentWeightNum ? currentWeightNum + ' kg' : 'Not recorded'}
- Target Weight: ${weightGoal ? weightGoal + ' kg' : 'Not recorded'}
- Height: ${userHeightCm ? userHeightCm + ' cm' : 'Not recorded'}
- Current BMI: ${bmi !== null ? bmi : 'Not computed'}
- ${fatDataText}
- Target Body Fat %: ${bodyFatGoal ? bodyFatGoal + '%' : 'Not recorded'}
- D1 Weight History Logs: ${JSON.stringify(sortedHistory.slice(-8).map(s => ({ date: s.date, weight: s.weight_kg, fat: s.body_fat_pct })))}

CRITICAL SCIENTIFIC RULE: Never claim 100% of weight loss will be fat; the physiological goal must be preserving maximum muscle mass while reducing fat stores.

CRITICAL INSTRUCTIONS:
1. Write the ENTIRE response in ${langName}.
2. Structure with clear Markdown headers:
   ### 1. Current Weight & BMI Analysis
   ### 2. Body Fat Status
   ### 3. Muscle Mass Preservation Protocol
   ### 4. 3 Practical Custom Recommendations`;

    try {
      const res = await sendWorkerAiFullRequest('nutrition', prompt, []);
      setAiBodyCompResult(res.reply || t('aiNoReply', 'پاسخی دریافت نشد.'));
    } catch (err: any) {
      setAiBodyCompResult(t('aiErrorPrefix', 'خطا در دریافت پاسخ از هوش مصنوعی: ') + (err?.message || t('tryAgain', 'لطفاً دوباره تلاش کنید.')));
    } finally {
      setIsAnalyzingBodyCompAi(false);
    }
  };

  // Muscle Preservation AI Handler
  const handleAskMusclePreserveAi = async () => {
    if (isAnalyzingMuscleAi) return;
    setIsAnalyzingMuscleAi(true);
    setAiMuscleResult(null);

    const langName = lang === 'sv' ? 'Swedish' : lang === 'fa' ? 'Persian' : 'English';

    const prompt = `You are a sports physiology and senior clinical nutrition specialist.
Provide a scientific, practical protocol on "How to preserve maximum muscle mass during weight loss" for this user:

User Profile:
- Current Weight: ${currentWeightNum || 'Unknown'} kg
- Body Fat %: ${currentFatNum ? currentFatNum + '%' : 'Not logged'}
- Height: ${userHeightCm || 'Unknown'} cm
- BMI: ${bmi !== null ? bmi : 'Unknown'}

CRITICAL INSTRUCTIONS:
1. Write the ENTIRE response in ${langName}.
2. Use clear Markdown headings:
   1. **Daily Protein Intake (grams per kg body weight)**
   2. **Resistance / Hypertrophy Training Principles**
   3. **Safe Caloric Deficit Range to Avoid Catabolism**
   4. **Stress, Sleep & Cortisol Management**
   5. **Warning Signs of Muscle Loss**`;

    try {
      const res = await sendWorkerAiFullRequest('nutrition', prompt, []);
      setAiMuscleResult(res.reply || t('aiNoReply', 'پاسخی دریافت نشد.'));
    } catch (err: any) {
      setAiMuscleResult(t('aiErrorPrefix', 'خطا در دریافت پاسخ از هوش مصنوعی: ') + (err?.message || t('tryAgain', 'لطفاً دوباره تلاش کنید.')));
    } finally {
      setIsAnalyzingMuscleAi(false);
    }
  };

  // SVG Chart rendering
  const renderChart = () => {
    if (sortedHistory.length === 0) {
      return (
        <div className="h-64 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-2xl bg-gray-50/50 dark:bg-gray-900/30 text-gray-400 p-6 text-center">
          <Scale className="w-10 h-10 mb-2 opacity-40 text-indigo-500 animate-pulse" />
          <p className="text-xs font-bold">{t('weightChartEmpty', 'هنوز رکوردی ثبت نشده است.')}</p>
          <p className="text-[11px] mt-1 text-gray-500">{t('weightChartEmptyDesc', 'با استفاده از فرم سمت راست، اولین وزن خود را ثبت کنید.')}</p>
        </div>
      );
    }

    const width = 600;
    const heightPx = 240;
    const paddingTop = 30;
    const paddingBottom = 40;
    const paddingLeft = 45;
    const paddingRight = 20;

    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = heightPx - paddingTop - paddingBottom;

    const pointsToDisplay = sortedHistory.map(item => ({
      date: item.date,
      value: selectedMetric === 'weight' ? item.weight_kg : (item.body_fat_pct || 0)
    })).filter(p => p.value > 0);

    if (pointsToDisplay.length === 0) {
      return (
        <div className="h-64 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-2xl bg-gray-50/50 dark:bg-gray-900/30 text-gray-400 p-6 text-center">
          <Percent className="w-10 h-10 mb-2 opacity-40 text-indigo-500" />
          <p className="text-xs font-bold">داده‌ای برای درصد چربی در این بازه وجود ندارد.</p>
        </div>
      );
    }

    const values = pointsToDisplay.map(p => p.value);
    const goalVal = selectedMetric === 'weight' ? parseFloat(weightGoal) : parseFloat(bodyFatGoal);

    let minVal = Math.min(...values);
    let maxVal = Math.max(...values);
    if (!isNaN(goalVal) && goalVal > 0) {
      minVal = Math.min(minVal, goalVal);
      maxVal = Math.max(maxVal, goalVal);
    }

    // Give padding to Y bounds
    minVal = Math.floor(minVal - 1);
    maxVal = Math.ceil(maxVal + 1);
    if (minVal === maxVal) {
      minVal -= 2;
      maxVal += 2;
    }

    const getX = (index: number) => {
      if (pointsToDisplay.length === 1) return paddingLeft + chartWidth / 2;
      return paddingLeft + (index / (pointsToDisplay.length - 1)) * chartWidth;
    };

    const getY = (val: number) => {
      return paddingTop + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
    };

    const pathD = pointsToDisplay.map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(p.value)}`).join(' ');
    const areaD = pointsToDisplay.length > 1
      ? `${pathD} L ${getX(pointsToDisplay.length - 1)} ${paddingTop + chartHeight} L ${getX(0)} ${paddingTop + chartHeight} Z`
      : '';

    const ticks = [minVal, minVal + (maxVal - minVal) * 0.33, minVal + (maxVal - minVal) * 0.66, maxVal];

    return (
      <div className="relative w-full overflow-hidden bg-gray-50/50 dark:bg-gray-900/40 p-2 rounded-2xl border border-gray-100 dark:border-gray-800">
        <svg viewBox={`0 0 ${width} ${heightPx}`} className="w-full h-auto overflow-visible">
          <defs>
            <linearGradient id="weightAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0.0" />
            </linearGradient>
            <linearGradient id="weightLineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#818cf8" />
              <stop offset="100%" stopColor="#4f46e5" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {ticks.map((tick, idx) => {
            const y = getY(tick);
            return (
              <g key={idx}>
                <line 
                  x1={paddingLeft} 
                  y1={y} 
                  x2={width - paddingRight} 
                  y2={y} 
                  stroke="currentColor" 
                  className="text-gray-200 dark:text-gray-800" 
                  strokeDasharray="3 3" 
                />
                <text 
                  x={paddingLeft - 8} 
                  y={y + 4} 
                  textAnchor="end" 
                  className="fill-gray-400 dark:fill-gray-500 font-mono text-[10px] font-semibold"
                >
                  {toPersianDigits(tick.toFixed(1))}
                </text>
              </g>
            );
          })}

          {/* Goal Line */}
          {!isNaN(goalVal) && goalVal > 0 && (
            <g className="opacity-80">
              <line 
                x1={paddingLeft} 
                y1={getY(goalVal)} 
                x2={width - paddingRight} 
                y2={getY(goalVal)} 
                stroke="#10b981" 
                strokeWidth="1.5" 
                strokeDasharray="5 5"
              />
              <text 
                x={width - paddingRight} 
                y={getY(goalVal) - 6} 
                textAnchor="end" 
                className="fill-emerald-500 font-bold text-[9px]"
              >
                {selectedMetric === 'weight'
                  ? t('weightGoalChartLabel', 'وزن هدف ({{weight}} kg)', { weight: toPersianDigits(goalVal) })
                  : `هدف درصد چربی (${toPersianDigits(goalVal)}%)`}
              </text>
            </g>
          )}

          {/* Path Area */}
          {pointsToDisplay.length > 1 && (
            <path d={areaD} fill="url(#weightAreaGrad)" />
          )}

          {/* Path Line */}
          {pointsToDisplay.length > 1 ? (
            <path d={pathD} fill="none" stroke="url(#weightLineGrad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <line x1={paddingLeft} y1={getY(values[0])} x2={width - paddingRight} y2={getY(values[0])} stroke="#6366f1" strokeWidth="1" strokeDasharray="2 2" className="opacity-30" />
          )}

          {/* Dots */}
          {pointsToDisplay.map((point, i) => {
            const x = getX(i);
            const y = getY(point.value);
            const isHovered = hoveredPoint?.date === point.date;
            const dateParts = point.date.split('-');
            const dateLabel = `${dateParts[1]}/${dateParts[2]}`;

            return (
              <g key={i} className="cursor-pointer" 
                 onMouseEnter={() => setHoveredPoint({ ...point, x, y })}
                 onMouseLeave={() => setHoveredPoint(null)}
              >
                <circle cx={x} cy={y} r="10" fill="transparent" />
                {isHovered && (
                  <circle cx={x} cy={y} r="8" fill="#6366f1" fillOpacity="0.2" className="animate-ping" />
                )}
                <circle 
                  cx={x} 
                  cy={y} 
                  r={isHovered ? "6" : "4.5"} 
                  fill={isHovered ? "#6366f1" : "white"} 
                  stroke="#6366f1" 
                  strokeWidth="2.5" 
                />
                
                {(pointsToDisplay.length < 15 || i % Math.ceil(pointsToDisplay.length / 8) === 0 || i === pointsToDisplay.length - 1) && (
                  <text 
                    x={x} 
                    y={paddingTop + chartHeight + 16} 
                    textAnchor="middle" 
                    className="fill-gray-400 dark:fill-gray-500 font-mono text-[9px] font-medium"
                  >
                    {toPersianDigits(dateLabel)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Custom Tooltip */}
        {hoveredPoint && (() => {
          const isNearTop = hoveredPoint.y < 55;
          const topPx = (hoveredPoint.y * 256) / 240 + (isNearTop ? 20 : -45);
          return (
            <div 
              className="absolute z-10 bg-gray-900/95 dark:bg-gray-950/95 text-white px-3 py-2 rounded-xl border border-gray-800 shadow-xl text-[10px] font-semibold text-center pointer-events-none transition-all duration-150"
              style={{ 
                left: `${(hoveredPoint.x / width) * 100}%`, 
                top: `${topPx}px`,
                transform: 'translateX(-50%)',
              }}
            >
              <div className="text-gray-300">{fmt(hoveredPoint.date)}</div>
              <div className="text-emerald-400 font-bold mt-0.5">
                {selectedMetric === 'weight'
                  ? `وزن: ${fmt(hoveredPoint.value)} کیلوگرم`
                  : `چربی: ${fmt(hoveredPoint.value)}٪`}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div id="weight-tracker-view" className="space-y-6 animate-fadeIn">
      
      {/* Header */}
      <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
        <div>
          <h2 className="text-2xl font-semibold font-display text-gray-900 dark:text-white flex items-center gap-2">
            <Scale className="w-6 h-6 text-indigo-500" />
            {t('weightHeaderTitle', 'ثبت وزن، درصد چربی و پایش شاخص BMI')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('weightHeaderDesc', 'داده‌های این صفحه مستقیماً در Cloudflare D1 ذخیره می‌شوند تا در تمامی دستگاه‌های شما همگام باقی بمانند.')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
          <button
            onClick={() => {
              if (weightLogs.length >= 1) {
                setCompareDate1(weightLogs[weightLogs.length - 1]?.date || '');
                setCompareDate2(weightLogs[0]?.date || '');
              }
              setShowCompareModal(true);
            }}
            className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
            title={t('photoCompareTitle', 'مقایسه تصویری و شاخص‌های پیشرفت اندام')}
          >
            <Camera className="w-4 h-4" />
            <span>{t('photoCompareBtn', 'مقایسه تصویری و شاخص‌ها')}</span>
          </button>

          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
            <Activity className="w-3.5 h-3.5" />
            {t('d1DataSourceTag', 'منبع داده: Cloudflare D1')}
          </span>
        </div>
      </div>

      {loadingD1 && (
        <div className="p-8 text-center bg-gray-50 dark:bg-gray-900/30 rounded-2xl border border-gray-100 dark:border-gray-800 flex items-center justify-center gap-3 text-sm text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin text-indigo-500" />
          <span>{t('fetchingD1HealthData', 'در حال دریافت جدیدترین داده‌های سلامت از Cloudflare D1...')}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Column: Log Entry and BMI / Body Fat (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          
          {/* Section 1: Log Weight & Body Fat */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <div className="flex items-center justify-between border-b pb-2.5 border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-500" />
                <h3 className="font-bold text-xs text-gray-400 uppercase tracking-wider">
                  {t('weightLogDate', 'ثبت شاخص‌ها برای تاریخ')}
                </h3>
              </div>
              <input 
                type="date" 
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-gray-50 dark:bg-gray-900 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700 focus:outline-none"
              />
            </div>

            {/* Weight Input Box */}
            <div className="flex flex-col items-center py-3 bg-gray-50/50 dark:bg-gray-900/20 rounded-2xl border border-gray-100 dark:border-gray-800/40">
              <span className="text-[10px] text-gray-400 font-semibold mb-2">{t('weightSpecify', 'وزن خود را مشخص کنید (کیلوگرم)')}</span>
              
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => adjustWeight(-1)}
                  className="w-9 h-9 flex items-center justify-center bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl transition-all cursor-pointer shadow-xs active:scale-95"
                  title="-۱ کیلوگرم"
                >
                  <Minus className="w-4 h-4 stroke-[3]" />
                </button>
                <button 
                  onClick={() => adjustWeight(-0.1)}
                  className="w-7 h-7 flex items-center justify-center bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg transition-all cursor-pointer shadow-xs text-[10px] font-bold active:scale-95"
                  title="-۰.۱ کیلوگرم"
                >
                  {toPersianDigits('-0.1')}
                </button>

                <div className="relative text-center mx-1 w-24">
                  <input 
                    type="number" 
                    step="0.1"
                    value={weightInput}
                    onChange={(e) => setWeightInput(e.target.value)}
                    className="w-full text-center text-2xl font-extrabold text-indigo-600 dark:text-indigo-400 bg-transparent focus:outline-none"
                  />
                  <span className="text-[9px] text-gray-400 font-bold block mt-0.5">{t('weightUnitKg', 'کیلوگرم (KG)')}</span>
                </div>

                <button 
                  onClick={() => adjustWeight(0.1)}
                  className="w-7 h-7 flex items-center justify-center bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg transition-all cursor-pointer shadow-xs text-[10px] font-bold active:scale-95"
                  title="+۰.۱ کیلوگرم"
                >
                  {toPersianDigits('+0.1')}
                </button>
                <button 
                  onClick={() => adjustWeight(1)}
                  className="w-9 h-9 flex items-center justify-center bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl transition-all cursor-pointer shadow-xs active:scale-95"
                  title="+۱ کیلوگرم"
                >
                  <Plus className="w-4 h-4 stroke-[3]" />
                </button>
              </div>
            </div>

            {/* Body Fat % Input Box */}
            <div className="flex flex-col items-center py-3 bg-gray-50/50 dark:bg-gray-900/20 rounded-2xl border border-gray-100 dark:border-gray-800/40">
              <span className="text-[10px] text-gray-400 font-semibold mb-2">{t('bodyFatPctLabel', 'درصد چربی بدن (اختیاری ٪)')}</span>
              
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => adjustBodyFat(-1)}
                  className="w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl transition-all cursor-pointer shadow-xs active:scale-95"
                  title="-۱ درصد"
                >
                  <Minus className="w-3.5 h-3.5 stroke-[3]" />
                </button>
                <button 
                  onClick={() => adjustBodyFat(-0.5)}
                  className="w-7 h-7 flex items-center justify-center bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg transition-all cursor-pointer shadow-xs text-[10px] font-bold active:scale-95"
                  title="-۰.۵ درصد"
                >
                  {formatNumberByLang(-0.5, lang)}
                </button>

                <div className="relative text-center mx-1 w-24">
                  <input 
                    type="number" 
                    step="0.1"
                    placeholder="18.5"
                    value={bodyFatInput}
                    onChange={(e) => setBodyFatInput(e.target.value)}
                    className="w-full text-center text-xl font-extrabold text-emerald-600 dark:text-emerald-400 bg-transparent focus:outline-none"
                  />
                  <span className="text-[9px] text-gray-400 font-bold block mt-0.5">{t('bodyFatPctUnit', 'درصد چربی (٪)')}</span>
                </div>

                <button 
                  onClick={() => adjustBodyFat(0.5)}
                  className="w-7 h-7 flex items-center justify-center bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg transition-all cursor-pointer shadow-xs text-[10px] font-bold active:scale-95"
                  title="+۰.۵ درصد"
                >
                  {formatNumberByLang(0.5, lang)}
                </button>
                <button 
                  onClick={() => adjustBodyFat(1)}
                  className="w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl transition-all cursor-pointer shadow-xs active:scale-95"
                  title="+۱ درصد"
                >
                  <Plus className="w-3.5 h-3.5 stroke-[3]" />
                </button>
              </div>
            </div>

            {/* Progress Photos Upload & Preview Box */}
            <div className="p-4 bg-gray-50/70 dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-gray-800/60 space-y-3">
              <div className="flex items-center justify-between border-b pb-2 border-gray-200/60 dark:border-gray-800">
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-indigo-500" />
                  <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                    {t('progressPhotosTitle', 'تصاویر پیشرفت اندام (عکس)')}
                  </span>
                </div>
                <span className="text-[10px] text-gray-400 font-mono">
                  {t('maxSizeNotice', 'حداکثر ۶۰۰ کیلوبایت')}
                </span>
              </div>

              {/* Photo Type Picker */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-[11px]">
                <span className="text-[10px] text-gray-400 font-bold shrink-0">{t('photoAngleLabel', 'زاویه:')}</span>
                {[
                  { id: 'front', label: t('angleFront', 'روبرو') },
                  { id: 'side', label: t('angleSide', 'نیم‌رخ') },
                  { id: 'back', label: t('angleBack', 'پشت') },
                  { id: 'other', label: t('angleOther', 'سایر') }
                ].map(angle => (
                  <button
                    key={angle.id}
                    type="button"
                    onClick={() => setSelectedPhotoType(angle.id as WeightPhotoType)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${
                      selectedPhotoType === angle.id
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {angle.label}
                  </button>
                ))}
              </div>

              {/* Existing Photos Grid for Selected Date */}
              {(() => {
                const matchRecord = weightLogs.find(m => m.date === selectedDate);
                const existingPhotos = matchRecord?.photos || [];

                if (existingPhotos.length === 0 && stagedPhotos.length === 0) {
                  return (
                    <div className="p-3 text-center rounded-xl bg-white/60 dark:bg-gray-900/60 border border-dashed border-gray-200 dark:border-gray-800 text-[11px] text-gray-400">
                      {t('noPhotosForDate', 'هیچ تصویری برای این تاریخ ثبت نشده است.')}
                    </div>
                  );
                }

                const photoTypeLabels: Record<string, string> = {
                  front: 'روبرو',
                  side: 'نیم‌رخ',
                  back: 'پشت',
                  other: 'سایر'
                };

                return (
                  <div className="grid grid-cols-3 gap-2">
                    {/* Saved Photos */}
                    {existingPhotos.map(photo => (
                      <div key={photo.id || photo.r2_key} className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-black aspect-square">
                        <img
                          src={photo.download_url}
                          alt={photo.file_name}
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => setActiveLightboxPhoto({ ...photo, date: selectedDate, weight_kg: matchRecord?.weight_kg })}
                        />
                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/70 text-white text-[8px] font-bold backdrop-blur-xs">
                          {photoTypeLabels[photo.photo_type || 'other'] || 'عکس'}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeletePhoto(photo.id)}
                          className="absolute top-1 left-1 p-1 bg-red-600/90 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 cursor-pointer"
                          title={t('deletePhotoTooltip', 'حذف عکس')}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}

                    {/* Staged Photos */}
                    {stagedPhotos.map((staged, idx) => (
                      <div key={idx} className="relative group rounded-xl overflow-hidden border border-amber-300 dark:border-amber-700 bg-black aspect-square">
                        <img
                          src={staged.previewUrl}
                          alt="پیش‌نمایش"
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-amber-600/90 text-white text-[8px] font-bold backdrop-blur-xs">
                          {photoTypeLabels[staged.photoType] || 'پیش‌نمایش'}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveStagedPhoto(idx)}
                          className="absolute top-1 left-1 p-1 bg-red-600/90 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 cursor-pointer"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Upload Input Button */}
              <label className="flex items-center justify-center gap-2 py-2 px-3 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800/80 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-2xs">
                {uploadingPhoto ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                <span>
                  {uploadingPhoto
                    ? t('uploadingPhotoMsg', 'در حال پردازش و آپلود تصویر...')
                    : t('addProgressPhotoBtn', '+ افزودن تصویر پیشرفت')}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                  className="hidden"
                />
              </label>
            </div>

            <button
              onClick={handleSaveWeight}
              disabled={saving}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-60"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? t('savingToD1Btn', 'در حال ذخیره‌سازی در Cloudflare D1...') : t('weightSaveBtn', 'ثبت و ذخیره شاخص‌ها در D1')}
            </button>
          </div>

          {/* Section 2: BMI Display */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <div className="flex items-center gap-2 border-b pb-2.5 border-gray-100 dark:border-gray-700">
              <Award className="w-5 h-5 text-emerald-500" />
              <h3 className="font-bold text-xs text-gray-400 uppercase tracking-wider">
                {t('weightBmiTitle', 'شاخص توده بدنی (BMI) فعلی شما')}
              </h3>
            </div>

            {!userHeightCm ? (
              <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl text-xs text-amber-800 dark:text-amber-200 flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold block mb-0.5">{t('weightBmiPending', 'محاسبه در انتظار قد شماست')}</span>
                  {t('weightBmiPendingDesc', 'برای محاسبه خودکار شاخص BMI، لطفاً قد خود را در کادر پایین وارد کرده و ذخیره نمایید.')}
                </div>
              </div>
            ) : !currentWeightNum ? (
              <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800/40 p-4 rounded-xl text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('weightBmiNeedWeight', 'برای نمایش BMI، ابتدا باید حداقل یک رکورد وزن ثبت کنید.')}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-900/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-800/30">
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold block">{t('weightBmiLabel', 'شاخص توده بدنی')}</span>
                    <span className="text-3xl font-extrabold text-gray-900 dark:text-white block mt-1">
                      {fmt(bmi!)}
                    </span>
                  </div>
                  <div className={`px-3 py-1.5 rounded-xl text-xs font-extrabold ${bmiBg} ${bmiColor}`}>
                    {bmiCategory}
                  </div>
                </div>

                {/* BMI Progress bar */}
                <div className="space-y-1">
                  <div className="flex justify-between text-[9px] text-gray-400 font-bold px-1">
                    <span>{toPersianDigits('15')}</span>
                    <span>{toPersianDigits('18.5')} ({t('weightBmiUnderweight', 'کم‌وزن')})</span>
                    <span>{toPersianDigits('25')} ({t('weightBmiHealthy', 'سلامت')})</span>
                    <span>{toPersianDigits('30')} ({t('weightBmiOverweight', 'اضافه‌وزن')})</span>
                    <span>{toPersianDigits('40')} ({t('weightBmiObeseCat', 'چاق')})</span>
                  </div>
                  <div className="w-full h-3 bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden relative border border-gray-200 dark:border-gray-800">
                    <div className="absolute top-0 bottom-0 left-0 w-[46.25%] bg-blue-300/20" title={t('weightBmiUnderweight', 'کم‌وزن')} />
                    <div className="absolute top-0 bottom-0 left-[46.25%] w-[16.25%] bg-emerald-500/20" title={t('weightBmiHealthy', 'سلامت')} />
                    <div className="absolute top-0 bottom-0 left-[62.5%] w-[12.5%] bg-orange-500/20" title={t('weightBmiOverweight', 'اضافه‌وزن')} />
                    <div className="absolute top-0 bottom-0 left-[75%] right-0 bg-red-500/20" title={t('weightBmiObeseCat', 'چاق')} />

                    <div 
                      className="absolute top-0 bottom-0 w-1.5 bg-indigo-600 dark:bg-indigo-400 rounded-full border border-white shadow-md transition-all duration-500"
                      style={{ left: bmiProgressWidth }}
                    />
                  </div>
                </div>

                <div className="p-3 bg-indigo-50 dark:bg-gray-900/40 rounded-xl border border-indigo-100 dark:border-indigo-950/40 flex gap-2.5">
                  <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                    {bmiAdvice}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Body Fat Percentage Display */}
          {currentFatNum !== null && (
            <div className="premium-card p-5 dark:bg-gray-800 space-y-3">
              <div className="flex items-center justify-between border-b pb-2 border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-emerald-500" />
                  <h3 className="font-bold text-xs text-gray-400 uppercase tracking-wider">
                    {t('currentBodyFatTitle', 'وضعیت درصد چربی بدن')}
                  </h3>
                </div>
                <span className={`px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-500/10 ${fatColor}`}>
                  {fatCategory}
                </span>
              </div>

              <div className="flex items-center justify-between pt-1">
                <div>
                  <span className="text-2xl font-black text-gray-900 dark:text-white">
                    {fmt(currentFatNum)} ٪
                  </span>
                  <span className="text-[11px] text-gray-400 block mt-0.5">{t('actualD1BodyFat', 'درصد چربی واقعی D1')}</span>
                </div>
                {bodyFatGoal && (
                  <div className="text-right">
                    <span className="text-xs text-emerald-500 font-bold block">
                      {t('goalPrefix', 'هدف:')} {fmt(bodyFatGoal)} ٪
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {t('diffPrefix', 'اختلاف:')} {fmt((currentFatNum - parseFloat(bodyFatGoal)).toFixed(1))} ٪
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 4: Physical Profile Settings (Height & Goals) */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <div className="flex items-center gap-2 border-b pb-2.5 border-gray-100 dark:border-gray-700">
              <Ruler className="w-5 h-5 text-indigo-500" />
              <h3 className="font-bold text-xs text-gray-400 uppercase tracking-wider">
                {t('weightProfileTitle', 'تنظیمات مشخصات فیزیکی مرجع (D1)')}
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase">
                  {t('weightProfileHeight', 'قد (سانتی‌متر)')}
                </label>
                <input 
                  type="number" 
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="178" 
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-950 dark:text-white font-mono font-bold"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase">
                  {t('weightProfileGoal', 'وزن هدف (kg)')}
                </label>
                <input 
                  type="number" 
                  value={weightGoal}
                  onChange={(e) => setWeightGoal(e.target.value)}
                  placeholder="70" 
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-950 dark:text-white font-mono font-bold"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase">
                  {t('targetBodyFatPctLabel', 'چربی هدف (٪)')}
                </label>
                <input 
                  type="number" 
                  value={bodyFatGoal}
                  onChange={(e) => setBodyFatGoal(e.target.value)}
                  placeholder="15" 
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:outline-none focus:border-indigo-500 text-gray-950 dark:text-white font-mono font-bold"
                />
              </div>
            </div>

            <button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-60"
            >
              {savingProfile ? <RefreshCw className="w-4 h-4 animate-spin" /> : (profileSaved ? <Check className="w-4 h-4" /> : <Target className="w-4 h-4" />)}
              {profileSaved ? t('weightProfileSaved', 'مشخصات در D1 ذخیره شد!') : t('weightProfileUpdateBtn', 'ذخیره مشخصات فیزیکی در Cloudflare D1')}
            </button>
          </div>

        </div>

        {/* Right Column: Weight History Graph and Table (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          
          {/* Section 5: Trend Graph */}
          <div className="premium-card p-6 dark:bg-gray-800 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b pb-3 border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-500" />
                <h3 className="font-bold text-sm text-gray-900 dark:text-white font-display">
                  {t('weightChartTitle', 'نمودار روند تغییرات')}
                </h3>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {/* Metric Selector Toggle */}
                <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-900 rounded-xl">
                  <button
                    onClick={() => setSelectedMetric('weight')}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all cursor-pointer ${
                      selectedMetric === 'weight'
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    {t('metricWeightToggle', 'تغییرات وزن (kg)')}
                  </button>
                  <button
                    onClick={() => setSelectedMetric('fat')}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all cursor-pointer ${
                      selectedMetric === 'fat'
                        ? 'bg-emerald-600 text-white shadow-xs'
                        : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    {t('metricFatToggle', 'درصد چربی (٪)')}
                  </button>
                </div>

                {/* Time Range Selector */}
                <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-900 rounded-xl">
                  {(['all', '30', '90'] as const).map((range) => (
                    <button
                      key={range}
                      onClick={() => setSelectedRange(range)}
                      className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all cursor-pointer ${
                        selectedRange === range 
                          ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-xs' 
                          : 'text-gray-400 hover:text-gray-700'
                      }`}
                    >
                      {range === 'all' ? t('weightChartFilterAll', 'همه زمان‌ها') : range === '30' ? t('filter30Days', '۳۰ روز') : t('filter90Days', '۹۰ روز')}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {renderChart()}

            {sortedHistory.length > 1 && (
              <div className="flex justify-between items-center bg-indigo-500/5 p-4 rounded-xl border border-indigo-100 dark:border-indigo-950/30 text-xs">
                <span className="text-gray-500 dark:text-gray-400">{t('weightTrendOverallStatus', 'وضعیت کلی روند وزن:')}</span>
                <div className="flex items-center gap-1 font-bold">
                  {sortedHistory[sortedHistory.length - 1].weight_kg < sortedHistory[0].weight_kg ? (
                    <span className="text-emerald-500 flex items-center gap-1">
                      <TrendingDown className="w-4 h-4" />
                      {t('weightTrendDown', 'کاهش {{weight}} کیلوگرم', { 
                        weight: fmt((sortedHistory[0].weight_kg - sortedHistory[sortedHistory.length - 1].weight_kg).toFixed(1)),
                        diff: fmt((sortedHistory[0].weight_kg - sortedHistory[sortedHistory.length - 1].weight_kg).toFixed(1))
                      })}
                    </span>
                  ) : sortedHistory[sortedHistory.length - 1].weight_kg > sortedHistory[0].weight_kg ? (
                    <span className="text-orange-500 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      {t('weightTrendUp', 'افزایش {{weight}} کیلوگرم', { 
                        weight: fmt((sortedHistory[sortedHistory.length - 1].weight_kg - sortedHistory[0].weight_kg).toFixed(1)),
                        diff: fmt((sortedHistory[sortedHistory.length - 1].weight_kg - sortedHistory[0].weight_kg).toFixed(1))
                      })}
                    </span>
                  ) : (
                    <span className="text-indigo-500 font-bold">
                      {t('weightTrendStable', 'بدون نوسان محسوس (ثبات وزن)', { weight: '۰', diff: '۰' })}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Section 6: Weight Records Timeline table from D1 */}
          <div className="premium-card p-5 dark:bg-gray-800 space-y-4">
            <div className="flex items-center justify-between border-b pb-2.5 border-gray-100 dark:border-gray-700">
              <h3 className="font-bold text-xs text-gray-400 uppercase tracking-wider">
                {t('weightHistoryTitle', 'جدول زمانی و تاریخچه ثبت‌ها در Cloudflare D1')}
              </h3>
              <span className="text-[10px] text-gray-400 font-mono font-bold">
                {t('totalRecordsCount', 'تعداد کل: {{count}}', { count: formatNumberByLang(weightLogs.length, lang) })}
              </span>
            </div>

            {weightLogs.length === 0 ? (
              <div className="p-4 rounded-xl text-center text-xs text-gray-400">
                {t('weightHistoryEmpty', 'جدول تاریخچه در D1 هنوز خالی است.')}
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto scrollbar-thin rounded-xl border border-gray-100 dark:border-gray-800/80">
                <table className="w-full border-collapse text-right text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 font-bold text-[10px] sticky top-0 border-b border-gray-100 dark:border-gray-800">
                    <tr>
                      <th className="p-3">{t('weightHistDate', 'تاریخ ثبت')}</th>
                      <th className="p-3">{t('weightHistWeight', 'وزن (kg)')}</th>
                      <th className="p-3">{t('weightHistBmi', 'شاخص BMI')}</th>
                      <th className="p-3">{t('bodyFatPctUnit', 'درصد چربی (٪)')}</th>
                      <th className="p-3">{t('weightHistPhotos', 'تصاویر')}</th>
                      <th className="p-3 text-left">{t('weightHistActions', 'عملیات')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800/50">
                    {weightLogs.map((p, idx) => {
                      let itemBmi: number | null = p.bmi || null;
                      if (!itemBmi && userHeightCm) {
                        const heightM = userHeightCm / 100;
                        itemBmi = parseFloat((p.weight_kg / (heightM * heightM)).toFixed(1));
                      }

                      return (
                        <tr key={p.id || idx} className="hover:bg-gray-50/40 dark:hover:bg-gray-800/20 font-medium">
                          <td className="p-3 text-gray-500 dark:text-gray-400 font-mono">
                            {fmt(p.date)}
                          </td>
                          <td className="p-3 text-gray-900 dark:text-white font-bold">
                            {fmt(p.weight_kg.toFixed(1))}
                          </td>
                          <td className="p-3 text-gray-500 dark:text-gray-400">
                            {itemBmi ? fmt(itemBmi) : '-'}
                          </td>
                          <td className="p-3 text-emerald-600 dark:text-emerald-400 font-bold">
                            {p.body_fat_pct ? fmt(p.body_fat_pct.toFixed(1)) + '٪' : '-'}
                          </td>
                          <td className="p-3">
                            {p.photos && p.photos.length > 0 ? (
                              <div className="flex items-center gap-1">
                                {p.photos.slice(0, 3).map((ph, pIdx) => (
                                  <img
                                    key={ph.id || pIdx}
                                    src={ph.download_url}
                                    alt={ph.file_name}
                                    className="w-7 h-7 object-cover rounded-md border border-gray-200 dark:border-gray-700 cursor-pointer hover:scale-110 transition-transform"
                                    onClick={() => setActiveLightboxPhoto({ ...ph, date: p.date, weight_kg: p.weight_kg })}
                                  />
                                ))}
                                {p.photos.length > 3 && (
                                  <span className="text-[9px] font-bold text-gray-400 bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">
                                    +{p.photos.length - 3}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600 text-[10px]">-</span>
                            )}
                          </td>
                          <td className="p-3 text-left">
                            <button
                              onClick={() => handleDeleteWeight(p.id || p.date)}
                              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-950/20 text-red-500 hover:text-red-600 rounded-lg transition-colors cursor-pointer"
                              title={t('weightHistDelTooltip', 'حذف این رکورد از D1')}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Section 7: Weight Loss Plateau Diagnostic & Action Plan */}
          <div className="premium-card p-6 dark:bg-gray-800 space-y-5 border-2 border-emerald-100 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50/20 via-white to-teal-50/10 dark:from-gray-800 dark:to-gray-900">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3 border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 shadow-xs">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-extrabold text-sm text-gray-900 dark:text-white font-display flex items-center gap-2">
                    <span>{t('bodyCompAiTitle', 'تحلیل هوشمند ترکیب بدن (Body Composition AI)')}</span>
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('bodyCompAiSubtitle', 'تحلیل هوشمند شاخص‌های وزن، چربی و عضلات بر اساس داده‌های واقعی D1')}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleAnalyzeBodyCompAi}
                  disabled={isAnalyzingBodyCompAi}
                  className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
                >
                  {isAnalyzingBodyCompAi ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  <span>{isAnalyzingBodyCompAi ? t('analyzingStateBtn', 'در حال تحلیل...') : t('analyzeCurrentStateBtn', 'تحلیل هوشمند وضعیت فعلی')}</span>
                </button>

                <button
                  onClick={handleAskMusclePreserveAi}
                  disabled={isAnalyzingMuscleAi}
                  className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
                >
                  {isAnalyzingMuscleAi ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                  <span>{isAnalyzingMuscleAi ? t('calculatingBtn', 'در حال محاسبه...') : t('askMusclePreserveBtn', 'چگونه عضله را حفظ کنم؟')}</span>
                </button>
              </div>
            </div>

            {/* Real Biometric Summary Chips */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
              <div className="p-3 bg-white dark:bg-gray-900/60 rounded-xl border border-gray-100 dark:border-gray-800">
                <span className="text-[10px] text-gray-400 font-bold block">{t('chipCurrentWeightD1', 'وزن فعلی D1')}</span>
                <span className="text-sm font-extrabold text-gray-900 dark:text-white mt-0.5 block">
                  {currentWeightNum ? `${fmt(currentWeightNum)} kg` : t('notLogged', 'ثبت نشده')}
                </span>
              </div>
              <div className="p-3 bg-white dark:bg-gray-900/60 rounded-xl border border-gray-100 dark:border-gray-800">
                <span className="text-[10px] text-gray-400 font-bold block">{t('chipWeightGoalD1', 'وزن هدف D1')}</span>
                <span className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5 block">
                  {weightGoal ? `${fmt(weightGoal)} kg` : t('notLogged', 'ثبت نشده')}
                </span>
              </div>
              <div className="p-3 bg-white dark:bg-gray-900/60 rounded-xl border border-gray-100 dark:border-gray-800">
                <span className="text-[10px] text-gray-400 font-bold block">{t('chipBodyFatCurrent', 'درصد چربی فعلی')}</span>
                <span className="text-sm font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 block">
                  {currentFatNum !== null ? `${fmt(currentFatNum)} ٪` : t('requiresDataLog', 'نیازمند ثبت داده')}
                </span>
              </div>
              <div className="p-3 bg-white dark:bg-gray-900/60 rounded-xl border border-gray-100 dark:border-gray-800">
                <span className="text-[10px] text-gray-400 font-bold block">{t('chipBodyFatGoalD1', 'چربی هدف D1')}</span>
                <span className="text-sm font-extrabold text-indigo-600 dark:text-indigo-400 mt-0.5 block">
                  {bodyFatGoal ? `${fmt(bodyFatGoal)} ٪` : t('notLogged', 'ثبت نشده')}
                </span>
              </div>
            </div>

            {/* AI Body Composition Analysis Output */}
            {aiBodyCompResult && (
              <div className="p-4 rounded-2xl bg-emerald-50/60 dark:bg-gray-900/90 border border-emerald-200 dark:border-emerald-800 text-xs leading-relaxed space-y-2 animate-fadeIn">
                <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-bold border-b border-emerald-200 dark:border-emerald-800 pb-2">
                  <Sparkles className="w-4 h-4 text-emerald-500" />
                  <span>{t('aiBodyCompResultTitle', 'نتیجه تحلیل هوشمند ترکیب بدن (پزشک متابولیک AI)')}</span>
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none text-gray-800 dark:text-gray-200">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {aiBodyCompResult}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            {/* AI Muscle Preservation Protocol Output */}
            {aiMuscleResult && (
              <div className="p-4 rounded-2xl bg-indigo-50/60 dark:bg-gray-900/90 border border-indigo-200 dark:border-indigo-800 text-xs leading-relaxed space-y-2 animate-fadeIn">
                <div className="flex items-center gap-2 text-indigo-800 dark:text-indigo-300 font-bold border-b border-indigo-200 dark:border-indigo-800 pb-2">
                  <ShieldAlert className="w-4 h-4 text-indigo-500" />
                  <span>{t('aiMuscleResultTitle', 'پروتکل تخصصی حفظ عضلات هنگام چربی‌سوزی')}</span>
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none text-gray-800 dark:text-gray-200">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {aiMuscleResult}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            {/* 3 Practical Recommendations Box */}
            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900/80 border border-emerald-100 dark:border-gray-800 space-y-2">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-bold text-xs">
                <Check className="w-4 h-4" />
                <span>{t('recomTitle', '۳ توصیه عملی برای کاهش چربی و حفظ عضله')}</span>
              </div>
              <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1.5 list-disc list-inside leading-relaxed font-medium">
                <li>{t('recom1Text', 'مصرف پروتئین کافی: روزانه ۱.۶ تا ۲.۲ گرم پروتئین به ازای هر کیلوگرم وزن بدن مصرف کنید تا جلوی کاتابولیسم و افت توده عضلانی گرفته شود.')}</li>
                <li>{t('recom2Text', 'تمرینات قدرتی/مقاومتی با بار فزاینده: حداقل ۳ جلسه در هفته تمرینات قدرتی انجام دهید تا سیگنال هیپرتروفی و حفظ عضله فعال بماند.')}</li>
                <li>{t('recom3Text', 'کسری کالری متعادل (۱۵ تا ۲۰٪): از رژیم‌های گرسنگی شدید پرهیز کنید تا بدن به جای عضله، از ذخایر چربی به‌عنوان انرژی اصلی استفاده کند.')}</li>
              </ul>
            </div>
          </div>

          {/* Section 8: Weight Loss Plateau Diagnostic & Action Plan */}
          <div className="premium-card p-6 dark:bg-gray-800 space-y-5 border-2 border-indigo-100 dark:border-indigo-900/50">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3 border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
                  <Brain className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-extrabold text-sm text-gray-900 dark:text-white font-display flex items-center gap-2">
                    <span>{t('weightPlateauTitle', 'تحلیل هوشمند علت استپ وزنی و راهکارهای عملی شکستن استپ')}</span>
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('weightPlateauDesc', 'اگر بیش از ۱ تا ۲ هفته وزن شما تغییر نکرده، این پروتکل کلینیکی به شما کمک می‌کند.')}
                  </p>
                </div>
              </div>

              <div className="shrink-0">
                {plateauAnalysis.isPlateau ? (
                  <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 flex items-center gap-1.5 animate-pulse">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>{t('weightPlateauDetectedBadge', 'استپ وزنی شناسایی شد')}</span>
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" />
                    <span>{t('weightPlateauNormalBadge', 'روند تغییرات نرمال')}</span>
                  </span>
                )}
              </div>
            </div>

            {/* AI Diagnostic Button */}
            <div className="space-y-3">
              <button
                onClick={handleAskPlateauAi}
                disabled={isAnalyzingPlateauAi}
                className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-2xl text-xs font-extrabold shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
              >
                {isAnalyzingPlateauAi ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 text-amber-300" />
                )}
                <span>
                  {isAnalyzingPlateauAi
                    ? t('weightPlateauAnalyzing', 'در حال تحلیل نوسانات وزن، کالری و فعالیت‌های شما...')
                    : t('weightPlateauAiAnalyzeBtn', 'تحلیل اختصاصی استپ وزنی من با هوش مصنوعی (AI Diagnostic)')}
                </span>
              </button>

              {aiPlateauResult && (
                <div className="p-4 rounded-2xl bg-indigo-50/60 dark:bg-gray-900/80 border border-indigo-200 dark:border-indigo-800 text-xs leading-relaxed space-y-2 animate-fadeIn">
                  <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300 font-bold border-b border-indigo-100 dark:border-indigo-800 pb-2">
                    <Brain className="w-4 h-4 text-indigo-500" />
                    <span>گزارش پروتکل اختصاصی شکستن استپ وزنی (AI Advisor)</span>
                  </div>
                  <div className="prose prose-sm dark:prose-invert max-w-none text-gray-800 dark:text-gray-200">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {aiPlateauResult}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>

            {/* 6 Physiological Cause Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              {[
                { title: t('weightPlateauCause1Title', 'کاهش کالری سوزی پایه (BMR)'), desc: t('weightPlateauCause1Desc', 'با کاهش وزن، بدن با راندمان بیشتری انرژی می‌سوزاند.'), icon: Flame, color: 'text-amber-500 bg-amber-50 dark:bg-amber-950/30' },
                { title: t('weightPlateauCause2Title', 'افت ناخودآگاه فعالیت غیرورزشی (NEAT)'), desc: t('weightPlateauCause2Desc', 'میزان حرکات روزمره ناخودآگاه کاهش می‌یابد.'), icon: Target, color: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-950/30' },
                { title: t('weightPlateauCause3Title', 'احتباس آب ناشی از کورتیزول و استرس'), desc: t('weightPlateauCause3Desc', 'رژیم‌های طولانی میزان هورمون کورتیزول را بالا می‌برند.'), icon: Droplets, color: 'text-blue-500 bg-blue-50 dark:bg-blue-950/30' },
                { title: t('weightPlateauCause4Title', 'عضله‌سازی همزمان با چربی‌سوزی'), desc: t('weightPlateauCause4Desc', 'اگر تمرینات قدرتی انجام می‌دهید، چربی کاهش ولی عضلات ترمیم می‌شوند.'), icon: Award, color: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30' },
                { title: t('weightPlateauCause5Title', 'کاهش هورمون لپتین'), desc: t('weightPlateauCause5Desc', 'سیگنال‌های گرسنگی افزایش و سوخت‌وساز کندتر می‌شود.'), icon: Zap, color: 'text-purple-500 bg-purple-50 dark:bg-purple-950/30' },
                { title: t('weightPlateauCause6Title', 'خطای محاسباتی کالری مصرفی'), desc: t('weightPlateauCause6Desc', 'گاهی سس‌ها، روغن‌ها یا میان‌وعده‌های کوچک محاسبه نمی‌شوند.'), icon: ShieldAlert, color: 'text-rose-500 bg-rose-50 dark:bg-rose-950/30' },
              ].map((cause, idx) => {
                const IconComp = cause.icon;
                return (
                  <div key={idx} className="p-3.5 rounded-2xl bg-gray-50/80 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 space-y-1.5 transition-all hover:border-indigo-200 dark:hover:border-indigo-800">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-xl ${cause.color}`}>
                        <IconComp className="w-4 h-4" />
                      </div>
                      <h4 className="font-extrabold text-xs text-gray-800 dark:text-gray-200">
                        {cause.title}
                      </h4>
                    </div>
                    <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">
                      {cause.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

      </div>

      {/* Lightbox Preview Modal */}
      {activeLightboxPhoto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="relative max-w-2xl w-full bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden shadow-2xl space-y-3 p-4">
            <div className="flex items-center justify-between border-b border-gray-800 pb-2">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <Camera className="w-4 h-4 text-indigo-400" />
                <span>
                  {activeLightboxPhoto.date ? `${fmt(activeLightboxPhoto.date)}` : 'تصویر پیشرفت'}
                  {activeLightboxPhoto.weight_kg ? ` (${fmt(activeLightboxPhoto.weight_kg)} kg)` : ''}
                </span>
                {activeLightboxPhoto.photo_type && (
                  <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 text-[10px]">
                    {activeLightboxPhoto.photo_type === 'front' ? 'روبرو' : activeLightboxPhoto.photo_type === 'side' ? 'نیم‌رخ' : activeLightboxPhoto.photo_type === 'back' ? 'پشت' : 'سایر'}
                  </span>
                )}
              </div>
              <button
                onClick={() => setActiveLightboxPhoto(null)}
                className="p-1 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center justify-center max-h-[70vh] overflow-hidden rounded-xl bg-black">
              <img
                src={activeLightboxPhoto.download_url}
                alt={activeLightboxPhoto.file_name}
                className="max-h-[70vh] w-auto object-contain"
              />
            </div>

            <div className="flex items-center justify-between text-xs text-gray-400 font-mono pt-1">
              <span>{activeLightboxPhoto.file_name} ({(activeLightboxPhoto.file_size / 1024).toFixed(1)} KB)</span>
              <a
                href={`${activeLightboxPhoto.download_url}?download=true`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 font-bold"
              >
                <Download className="w-3.5 h-3.5" />
                <span>دانلود تصویر</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Side-by-Side Photo & Metrics Comparison Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto animate-fadeIn">
          <div className="relative max-w-4xl w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl shadow-2xl space-y-5 p-6 my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                  <ArrowLeftRight className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900 dark:text-white font-display">
                    {t('photoCompareModalTitle', 'مقایسه تصویری و شاخص‌های پیشرفت اندام')}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('photoCompareModalSub', 'انتخاب دو تاریخ مجزا جهت مقایسه تغییرات ظاهری، وزن، درصد چربی و شاخص BMI')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCompareModal(false)}
                className="p-1.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-300 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Date Selectors & Photo Filter Bar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-gray-50 dark:bg-gray-950/60 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
              {/* Select Date 1 */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                  {t('compareDate1Label', 'مبدا (تاریخ اول):')}
                </label>
                <select
                  value={compareDate1}
                  onChange={(e) => setCompareDate1(e.target.value)}
                  className="w-full text-xs font-bold p-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none"
                >
                  <option value="">{t('selectDatePlaceholder', '-- انتخاب تاریخ --')}</option>
                  {weightLogs.map(m => (
                    <option key={m.id} value={m.date}>
                      {fmt(m.date)} — {fmt(m.weight_kg)} kg {m.photos && m.photos.length > 0 ? `(📷 ${m.photos.length})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Date 2 */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                  {t('compareDate2Label', 'مقصد (تاریخ دوم):')}
                </label>
                <select
                  value={compareDate2}
                  onChange={(e) => setCompareDate2(e.target.value)}
                  className="w-full text-xs font-bold p-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none"
                >
                  <option value="">{t('selectDatePlaceholder', '-- انتخاب تاریخ --')}</option>
                  {weightLogs.map(m => (
                    <option key={m.id} value={m.date}>
                      {fmt(m.date)} — {fmt(m.weight_kg)} kg {m.photos && m.photos.length > 0 ? `(📷 ${m.photos.length})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Photo Filter Tabs */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                  {t('filterAngleLabel', 'فیلتر زاویه تصویر:')}
                </label>
                <div className="flex items-center gap-1 overflow-x-auto pb-0.5">
                  {[
                    { id: 'all', label: 'همه' },
                    { id: 'front', label: 'روبرو' },
                    { id: 'side', label: 'نیم‌رخ' },
                    { id: 'back', label: 'پشت' },
                    { id: 'other', label: 'سایر' }
                  ].map(f => (
                    <button
                      key={f.id}
                      onClick={() => setComparePhotoTypeFilter(f.id)}
                      className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${
                        comparePhotoTypeFilter === f.id
                          ? 'bg-indigo-600 text-white shadow-xs'
                          : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Comparison Side-by-Side Cards */}
            {(() => {
              const rec1 = weightLogs.find(m => m.date === compareDate1);
              const rec2 = weightLogs.find(m => m.date === compareDate2);

              if (!rec1 || !rec2) {
                return (
                  <div className="p-8 text-center bg-gray-50 dark:bg-gray-950 rounded-2xl border border-gray-100 dark:border-gray-800 text-xs text-gray-400">
                    {t('selectBothDatesNotice', 'لطفاً دو تاریخ متداول را از کشوهای بالا جهت مقایسه انتخاب کنید.')}
                  </div>
                );
              }

              const filterPhotos = (photos?: WeightMeasurementPhoto[]) => {
                if (!photos) return [];
                if (comparePhotoTypeFilter === 'all') return photos;
                return photos.filter(p => (p.photo_type || 'other') === comparePhotoTypeFilter);
              };

              const photos1 = filterPhotos(rec1.photos);
              const photos2 = filterPhotos(rec2.photos);

              const weightDiff = parseFloat((rec2.weight_kg - rec1.weight_kg).toFixed(1));
              const fatDiff = rec1.body_fat_pct && rec2.body_fat_pct
                ? parseFloat((rec2.body_fat_pct - rec1.body_fat_pct).toFixed(1))
                : null;
              
              let bmi1 = rec1.bmi;
              let bmi2 = rec2.bmi;
              if (!bmi1 && userHeightCm) {
                const hM = userHeightCm / 100;
                bmi1 = parseFloat((rec1.weight_kg / (hM * hM)).toFixed(1));
              }
              if (!bmi2 && userHeightCm) {
                const hM = userHeightCm / 100;
                bmi2 = parseFloat((rec2.weight_kg / (hM * hM)).toFixed(1));
              }
              const bmiDiff = bmi1 && bmi2 ? parseFloat((bmi2 - bmi1).toFixed(1)) : null;

              return (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Card Date 1 */}
                    <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-950/80 border border-gray-200 dark:border-gray-800 space-y-3">
                      <div className="flex justify-between items-center border-b pb-2 border-gray-200 dark:border-gray-800">
                        <span className="font-extrabold text-sm text-indigo-600 dark:text-indigo-400 font-mono">
                          {fmt(rec1.date)}
                        </span>
                        <span className="text-[10px] font-bold text-gray-400 bg-white dark:bg-gray-900 px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-800">
                          {t('baselineDateBadge', 'مبدا')}
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                          <span className="text-[9px] text-gray-400 block">{t('weightUnitKg', 'وزن')}</span>
                          <span className="font-extrabold text-gray-900 dark:text-white mt-0.5 block">{fmt(rec1.weight_kg)} kg</span>
                        </div>
                        <div className="p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                          <span className="text-[9px] text-gray-400 block">{t('bodyFatPctUnit', 'چربی')}</span>
                          <span className="font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5 block">
                            {rec1.body_fat_pct ? fmt(rec1.body_fat_pct) + '٪' : '-'}
                          </span>
                        </div>
                        <div className="p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                          <span className="text-[9px] text-gray-400 block">{t('weightHistBmi', 'BMI')}</span>
                          <span className="font-extrabold text-gray-900 dark:text-white mt-0.5 block">{bmi1 ? fmt(bmi1) : '-'}</span>
                        </div>
                      </div>

                      {/* Photo Gallery Date 1 */}
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 block mb-1.5">
                          {t('photosForDateLabel', 'تصاویر ({{count}}):', { count: formatNumberByLang(photos1.length, lang) })}
                        </span>
                        {photos1.length === 0 ? (
                          <div className="p-4 text-center rounded-xl bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-800 text-[11px] text-gray-400">
                            {t('noPhotosInCategory', 'هیچ تصویری در این زوایه یافت نشد.')}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {photos1.map(p => (
                              <div key={p.id} className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-black aspect-4/3">
                                <img
                                  src={p.download_url}
                                  alt={p.file_name}
                                  className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                                  onClick={() => setActiveLightboxPhoto({ ...p, date: rec1.date, weight_kg: rec1.weight_kg })}
                                />
                                <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/70 text-white text-[8px] font-bold backdrop-blur-xs">
                                  {p.photo_type === 'front' ? 'روبرو' : p.photo_type === 'side' ? 'نیم‌رخ' : p.photo_type === 'back' ? 'پشت' : 'سایر'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Card Date 2 */}
                    <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-950/80 border border-gray-200 dark:border-gray-800 space-y-3">
                      <div className="flex justify-between items-center border-b pb-2 border-gray-200 dark:border-gray-800">
                        <span className="font-extrabold text-sm text-indigo-600 dark:text-indigo-400 font-mono">
                          {fmt(rec2.date)}
                        </span>
                        <span className="text-[10px] font-bold text-gray-400 bg-white dark:bg-gray-900 px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-800">
                          {t('comparisonDateBadge', 'مقصد')}
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                          <span className="text-[9px] text-gray-400 block">{t('weightUnitKg', 'وزن')}</span>
                          <span className="font-extrabold text-gray-900 dark:text-white mt-0.5 block">{fmt(rec2.weight_kg)} kg</span>
                        </div>
                        <div className="p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                          <span className="text-[9px] text-gray-400 block">{t('bodyFatPctUnit', 'چربی')}</span>
                          <span className="font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5 block">
                            {rec2.body_fat_pct ? fmt(rec2.body_fat_pct) + '٪' : '-'}
                          </span>
                        </div>
                        <div className="p-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
                          <span className="text-[9px] text-gray-400 block">{t('weightHistBmi', 'BMI')}</span>
                          <span className="font-extrabold text-gray-900 dark:text-white mt-0.5 block">{bmi2 ? fmt(bmi2) : '-'}</span>
                        </div>
                      </div>

                      {/* Photo Gallery Date 2 */}
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 block mb-1.5">
                          {t('photosForDateLabel', 'تصاویر ({{count}}):', { count: formatNumberByLang(photos2.length, lang) })}
                        </span>
                        {photos2.length === 0 ? (
                          <div className="p-4 text-center rounded-xl bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-800 text-[11px] text-gray-400">
                            {t('noPhotosInCategory', 'هیچ تصویری در این زوایه یافت نشد.')}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {photos2.map(p => (
                              <div key={p.id} className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-black aspect-4/3">
                                <img
                                  src={p.download_url}
                                  alt={p.file_name}
                                  className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                                  onClick={() => setActiveLightboxPhoto({ ...p, date: rec2.date, weight_kg: rec2.weight_kg })}
                                />
                                <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/70 text-white text-[8px] font-bold backdrop-blur-xs">
                                  {p.photo_type === 'front' ? 'روبرو' : p.photo_type === 'side' ? 'نیم‌رخ' : p.photo_type === 'back' ? 'پشت' : 'سایر'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Progress Delta Summary Banner */}
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-indigo-50/80 via-white to-emerald-50/80 dark:from-indigo-950/40 dark:via-gray-900 dark:to-emerald-950/40 border border-indigo-100 dark:border-indigo-900/50 flex flex-wrap items-center justify-around gap-4 text-xs font-bold">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">{t('deltaWeightLabel', 'تغییرات وزن:')}</span>
                      <span className={`px-2.5 py-1 rounded-xl font-extrabold ${
                        weightDiff < 0
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                          : weightDiff > 0
                          ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600'
                      }`}>
                        {weightDiff < 0 ? `کاهش ${fmt(Math.abs(weightDiff))} kg` : weightDiff > 0 ? `افزایش ${fmt(weightDiff)} kg` : 'بدون تغییر'}
                      </span>
                    </div>

                    {fatDiff !== null && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 dark:text-gray-400">{t('deltaFatLabel', 'تغییرات درصد چربی:')}</span>
                        <span className={`px-2.5 py-1 rounded-xl font-extrabold ${
                          fatDiff < 0
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                            : fatDiff > 0
                            ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600'
                        }`}>
                          {fatDiff < 0 ? `کاهش ${fmt(Math.abs(fatDiff))} ٪` : fatDiff > 0 ? `افزایش ${fmt(fatDiff)} ٪` : 'بدون تغییر'}
                        </span>
                      </div>
                    )}

                    {bmiDiff !== null && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 dark:text-gray-400">{t('deltaBmiLabel', 'تغییرات BMI:')}</span>
                        <span className={`px-2.5 py-1 rounded-xl font-extrabold ${
                          bmiDiff < 0
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                            : bmiDiff > 0
                            ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600'
                        }`}>
                          {bmiDiff < 0 ? `کاهش ${fmt(Math.abs(bmiDiff))}` : bmiDiff > 0 ? `افزایش ${fmt(bmiDiff)}` : 'بدون تغییر'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

    </div>
  );
}
